/**
 * =====================================================================
 * IMPORTANT - READ BEFORE MODIFYING ANY LIST ENDPOINT IN THIS FILE:
 * =====================================================================
 *
 * TNN's Django backend paginates ALL list endpoints by default:
 *
 *   API returns: { "count": N, "results": [...actual data...] }
 *   baseApi unwraps to: { "count": N, "results": [...] }  (NOT an array)
 *
 * Every ``build.query<Type[], ...>`` endpoint MUST have:
 *
 *   transformResponse: (r: { results?: Type[] }) =>
 *     Array.isArray(r?.results) ? r.results : [],
 *
 * Without this, ``.map()`` on the paginated object either crashes
 * ("TypeError: x.map is not a function") or silently shows nothing.
 * This was the #1 bug class in the v1.6 and v4.0.1 QA reports.
 * =====================================================================
 */
/**
 * Volunteer hours API endpoints.
 *
 * Mirrors the backend's /api/v1/people/volunteer-shifts/ and
 * /volunteer-programs/ surface. Writes are staff-only (enforced by the
 * backend); the UI hides write affordances for non-staff users.
 */
import { baseApi } from "@/app/store/api/baseApi";

import type { Paginated } from "@/shared/api/types";

import type {
  VolunteerHoursSummaryResponse,
  VolunteerProgram,
  VolunteerShift,
  ProgramEnrollmentRosterEntry,
} from "../model/types";

/** One recurring slot in a program's weekly pattern. */

/** Payload of /volunteer-shifts/active/. */
export interface ActiveShift {
  program: number | null;
  program_name: string | null;
  allocated_hours_per_shift: string | null;
  /**
   * Today's rostered shift, or null when there isn't one. Hours can only be
   * logged against a shift, so this is what the clock runs against.
   */
  scheduled_shift: {
    id: number;
    starts_at: string;
    ends_at: string;
    location: string;
  } | null;
  can_clock_in: boolean;
  /** Why the clock is unavailable, in words fit to show. */
  blocked_reason: string | null;
  /** The shift currently running, or null when the clock is stopped. */
  shift: VolunteerShift | null;
}

interface ShiftListParams {
  person?: number;
  person_role?: number;
  status?: string;
  program?: number;
  date_from?: string;
  date_to?: string;
  page_size?: number;
  ordering?: string;
}

interface ShiftCreateBody {
  person_role: number;
  date: string;
  hours: string;
  activity?: string;
  program?: number | null;
  /** Writable server-side; used when an approver adjusts the logged hours. */
  note?: string;
}

/** RTK Query endpoints for volunteer programs and shifts. */

/** A single occurrence of a program (v4.1.5). */
export interface ProgramSession {
  id: number;
  program: number;
  date: string;
  start_time: string | null;
  end_time: string | null;
  facilitator_id: number | null;
  facilitator_name: string;
  topic: string;
  notes: string;
  created_at: string;
  attendee_count?: number;
}

export interface SessionAttendanceRecord {
  id: number;
  session: number;
  person: number;
  person_name?: string;
  status: "present" | "absent" | "excused" | "late";
}

/** Query params for `/people/volunteer-programs/` (paginated). */
export interface VolunteerProgramListParams {
  page?: number;
  page_size?: number;
  coding?: boolean;
  search?: string;
}

export const volunteerApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listVolunteerShifts: build.query<Paginated<VolunteerShift>, ShiftListParams>({
      query: (params) => ({
        url: "/people/volunteer-shifts/",
        params: params as Record<string, string | number>,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({
                type: "VolunteerShift" as const,
                id,
              })),
              { type: "VolunteerShift", id: "LIST" },
            ]
          : [{ type: "VolunteerShift", id: "LIST" }],
    }),

    createVolunteerShift: build.mutation<VolunteerShift, ShiftCreateBody>({
      query: (body) => ({
        url: "/people/volunteer-shifts/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerShift", id: "SUMMARY" },
        { type: "VolunteerProgram" },
      ],
    }),

    updateVolunteerShift: build.mutation<
      VolunteerShift,
      { id: number; data: Partial<ShiftCreateBody> }
    >({
      query: ({ id, data }) => ({
        url: `/people/volunteer-shifts/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "VolunteerShift", id },
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerProgram" },
      ],
    }),

    deleteVolunteerShift: build.mutation<void, number>({
      query: (id) => ({
        url: `/people/volunteer-shifts/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerProgram" },
      ],
    }),

    verifyVolunteerShift: build.mutation<VolunteerShift, number>({
      query: (id) => ({
        url: `/people/volunteer-shifts/${id}/verify/`,
        method: "POST",
      }),
      invalidatesTags: (_, __, id) => [
        { type: "VolunteerShift", id },
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerProgram" },
        { type: "CheckIn", id: "DESK" },
        { type: "CheckIn", id: "SESSION" },
      ],
    }),

    rejectVolunteerShift: build.mutation<
      VolunteerShift,
      { id: number; note?: string; reason?: string; proposed_hours?: string }
    >({
      query: ({ id, note, reason, proposed_hours }) => ({
        url: `/people/volunteer-shifts/${id}/reject/`,
        method: "POST",
        body: {
          ...(note ? { note } : {}),
          ...(reason ? { reason } : {}),
          ...(proposed_hours != null && proposed_hours !== ""
            ? { proposed_hours }
            : {}),
        },
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "VolunteerShift", id },
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerProgram" },
        { type: "CheckIn", id: "DESK" },
        { type: "CheckIn", id: "SESSION" },
      ],
    }),

    getVolunteerHoursSummary: build.query<
      VolunteerHoursSummaryResponse,
      { date_from?: string; date_to?: string }
    >({
      query: (params) => ({
        url: "/people/volunteer-shifts/summary/",
        params: params as Record<string, string>,
      }),
      providesTags: [{ type: "VolunteerShift", id: "SUMMARY" }],
    }),

    listVolunteerPrograms: build.query<
      Paginated<VolunteerProgram>,
      VolunteerProgramListParams | void
    >({
      query: (params) => ({
        url: "/people/volunteer-programs/",
        params: {
          ...(params?.page != null ? { page: String(params.page) } : {}),
          ...(params?.page_size != null
            ? { page_size: String(params.page_size) }
            : {}),
          ...(params?.coding ? { coding: "true" } : {}),
          ...(params?.search?.trim() ? { search: params.search.trim() } : {}),
        },
      }),
      providesTags: [{ type: "VolunteerProgram", id: "LIST" }],
    }),

    createVolunteerProgram: build.mutation<
      VolunteerProgram,
      { name: string; description?: string; allocated_hours_per_shift?: string }
    >({
      query: (body) => ({
        url: "/people/volunteer-programs/",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "VolunteerProgram", id: "LIST" }],
    }),

    updateVolunteerProgram: build.mutation<
      VolunteerProgram,
      { id: number; data: Partial<VolunteerProgram> }
    >({
      query: ({ id, data }) => ({
        url: `/people/volunteer-programs/${id}/`,
        method: "PATCH",
        body: data,
      }),
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        if (data.is_active === undefined) return;

        const patch = dispatch(
          volunteerApi.util.updateQueryData(
            "listVolunteerPrograms",
            undefined,
            (draft) => {
              const program = draft.results.find((p) => p.id === id);
              if (program) program.is_active = data.is_active!;
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: [{ type: "VolunteerProgram", id: "LIST" }],
    }),

    // ── Program Sessions (v4.1.5) ─────────────────────────────────────
    listProgramSessions: build.query<ProgramSession[], number>({
      query: (programId) => `/people/program-sessions/?program=${programId}`,
      transformResponse: (r: { results?: ProgramSession[] } | ProgramSession[]) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r, _e, programId) => [{ type: "VolunteerProgram", id: `sessions-${programId}` }],
    }),

    createProgramSession: build.mutation<
      ProgramSession,
      { programId: number; data: Partial<ProgramSession> }
    >({
      query: ({ programId, data }) => ({
        url: "/people/program-sessions/",
        method: "POST",
        body: { ...data, program: programId },
      }),
      invalidatesTags: (_r, _e, { programId }) => [
        { type: "VolunteerProgram", id: `sessions-${programId}` },
      ],
    }),

    listSessionAttendance: build.query<SessionAttendanceRecord[], number>({
      query: (sessionId) => `/people/program-sessions/${sessionId}/attendance/`,
      transformResponse: (r: SessionAttendanceRecord[] | { results?: SessionAttendanceRecord[] }) =>
        Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : [],
      providesTags: (_r, _e, sessionId) => [{ type: "VolunteerProgram", id: `attendance-${sessionId}` }],
    }),

    bulkSetAttendance: build.mutation<
      SessionAttendanceRecord[],
      { sessionId: number; attendees: { person: number; status: string }[] }
    >({
      query: ({ sessionId, attendees }) => ({
        url: `/people/program-sessions/${sessionId}/attendance/`,
        method: "POST",
        body: { attendees },
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: "VolunteerProgram", id: `attendance-${sessionId}` },
      ],
    }),

    programEnrollmentRoster: build.query<ProgramEnrollmentRosterEntry[], number>({
      query: (programId) => `/people/program-enrollments/roster/${programId}/`,
      transformResponse: (
        r: ProgramEnrollmentRosterEntry[] | { results?: ProgramEnrollmentRosterEntry[] },
      ) => (Array.isArray(r) ? r : Array.isArray(r?.results) ? r.results : []),
      providesTags: (_r, _e, programId) => [
        { type: "VolunteerProgram", id: `roster-${programId}` },
      ],
    }),

    createProgramEnrollment: build.mutation<
      ProgramEnrollmentRosterEntry,
      { person: number; program: number; role?: string }
    >({
      query: (body) => ({
        url: "/people/program-enrollments/",
        method: "POST",
        body,
      }),
      invalidatesTags: (_r, _e, { program, person }) => [
        { type: "VolunteerProgram", id: `roster-${program}` },
        { type: "VolunteerProgram", id: "LIST" },
        { type: "Person", id: person },
      ],
    }),

    /**
     * Remove somebody from a program.
     *
     * `program` is not sent to the server - it is carried only so the right
     * roster cache entry can be invalidated afterwards.
     */
    /**
     * What the Time Tracker needs before it can draw anything: the program
     * this person is drafted into, and the shift they have running, if any.
     */
    // listShiftTemplates / createShiftTemplate / deleteShiftTemplate lived
    // here and hit /people/program-shift-templates/. That endpoint is gone:
    // people.0017 deletes ProgramShiftTemplate, because weekly patterns are
    // now scheduling.ScheduleTemplate. Use useListTemplatesQuery and friends
    // from features/scheduling/api/schedulingApi instead.

    getActiveShift: build.query<ActiveShift, void>({
      query: () => "/people/volunteer-shifts/active/",
      providesTags: [{ type: "VolunteerShift", id: "ACTIVE" }],
    }),

    clockIn: build.mutation<VolunteerShift, { activity?: string }>({
      query: (body) => ({
        url: "/people/volunteer-shifts/clock-in/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "VolunteerShift", id: "ACTIVE" },
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerProgram", id: "LIST" },
        { type: "VolunteerProgram" },
        { type: "CheckIn", id: "DESK" },
      ],
    }),

    clockOut: build.mutation<VolunteerShift, { activity?: string }>({
      query: (body) => ({
        url: "/people/volunteer-shifts/clock-out/",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "VolunteerShift", id: "ACTIVE" },
        { type: "VolunteerShift", id: "LIST" },
        { type: "VolunteerProgram", id: "LIST" },
        { type: "VolunteerProgram" },
        { type: "CheckIn", id: "DESK" },
      ],
    }),

    pauseClock: build.mutation<VolunteerShift, void>({
      query: () => ({
        url: "/people/volunteer-shifts/pause/",
        method: "POST",
        body: {},
      }),
      invalidatesTags: [
        { type: "VolunteerShift", id: "ACTIVE" },
        { type: "VolunteerShift", id: "LIST" },
        { type: "CheckIn", id: "DESK" },
      ],
    }),

    resumeClock: build.mutation<VolunteerShift, void>({
      query: () => ({
        url: "/people/volunteer-shifts/resume/",
        method: "POST",
        body: {},
      }),
      invalidatesTags: [
        { type: "VolunteerShift", id: "ACTIVE" },
        { type: "VolunteerShift", id: "LIST" },
        { type: "CheckIn", id: "DESK" },
      ],
    }),

    deleteProgramEnrollment: build.mutation<
      void,
      { id: number; program: number; person?: number }
    >({
      query: ({ id }) => ({
        url: `/people/program-enrollments/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { program, person }) => [
        { type: "VolunteerProgram", id: `roster-${program}` },
        { type: "VolunteerProgram", id: "LIST" },
        ...(person != null ? [{ type: "Person" as const, id: person }] : []),
      ],
    }),

    /**
     * Appoint or step down a program lead.
     *
     * The server rejects a non-staff lead, so a 400 here is the rule being
     * enforced rather than a failure to surface.
     */
    updateProgramEnrollment: build.mutation<
      ProgramEnrollmentRosterEntry,
      { id: number; program: number; role: "member" | "lead"; person?: number }
    >({
      query: ({ id, role }) => ({
        url: `/people/program-enrollments/${id}/`,
        method: "PATCH",
        body: { role },
      }),
      invalidatesTags: (_r, _e, { program, person }) => [
        { type: "VolunteerProgram", id: `roster-${program}` },
        { type: "VolunteerProgram", id: "LIST" },
        ...(person != null ? [{ type: "Person" as const, id: person }] : []),
      ],
    }),
  }),
});

export const {
  useListVolunteerShiftsQuery,
  useCreateVolunteerShiftMutation,
  useUpdateVolunteerShiftMutation,
  useDeleteVolunteerShiftMutation,
  useVerifyVolunteerShiftMutation,
  useRejectVolunteerShiftMutation,
  useGetVolunteerHoursSummaryQuery,
  useListVolunteerProgramsQuery,
  useLazyListVolunteerProgramsQuery,
  useCreateVolunteerProgramMutation,
  useUpdateVolunteerProgramMutation,
  useListProgramSessionsQuery,
  useCreateProgramSessionMutation,
  useListSessionAttendanceQuery,
  useBulkSetAttendanceMutation,
  useProgramEnrollmentRosterQuery,
  useClockInMutation,
  useClockOutMutation,
  usePauseClockMutation,
  useResumeClockMutation,
  useGetActiveShiftQuery,
  useCreateProgramEnrollmentMutation,
  useDeleteProgramEnrollmentMutation,
  useUpdateProgramEnrollmentMutation,
} = volunteerApi;
